import { FC, useState } from "react";
import {
  Box,
  Button,
  Container,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import { useFormik } from "formik";
import { toFormikValidationSchema } from "zod-formik-adapter";
import { useNavigate } from "react-router-dom";
import { z } from "zod";
import { adminPatchCurrentOrganizationReqDtoSchema } from "service";
import { makeSxStyles } from "ui";
import { AdRoutes } from "@/constants/ad-routes";
import FullScreenAuthLayout from "@/shared/components/FullScreenAuthLayout";
import useUpdateReplyEmail from "../hooks/useUpdateReplyEmail";

// Shown once, right after an invited org admin sets their password, only
// when their organization doesn't already have a customer reply email (see
// accept-invite-page.tsx). Matches the dedicated Figma mockup at node
// 15043:88615 - "Where should customer replies go?" / "Save and Continue" /
// "Continue Without Email" (not the generic Password-screen styling used
// elsewhere in the auth flow).
// Derived from the generated PATCH-current-organization schema (which keeps
// replyToEmail nullable, since the API also supports clearing it) - this
// screen's own "Save and Continue" action needs the stricter non-empty rule;
// "Continue Without Email" is the separate, explicit skip path.
const validationSchema = adminPatchCurrentOrganizationReqDtoSchema.extend({
  replyToEmail: z.string().email("Invalid email"),
});

const ReplyEmailSetupPage: FC = () => {
  const navigate = useNavigate();
  const styles = useStyles();

  const goToDashboard = () => navigate(AdRoutes.customer);

  const { updateReplyEmail, isLoading } = useUpdateReplyEmail(goToDashboard);

  const [blurredSinceEdit, setBlurredSinceEdit] = useState(false);

  const { values, handleChange, handleBlur, errors, isValid, submitForm } =
    useFormik({
      initialValues: { replyToEmail: "" },
      validateOnMount: true,
      validationSchema: toFormikValidationSchema(validationSchema),
      onSubmit: (formValues) => {
        updateReplyEmail(formValues.replyToEmail);
      },
    });

  const showError =
    blurredSinceEdit && !!errors.replyToEmail && !!values.replyToEmail;

  return (
    <FullScreenAuthLayout>
      <Container maxWidth="xs">
        <Box marginBottom="40px" display="flex" justifyContent="center">
          <img src="/snowsync-logo.png" alt="logo" style={{ width: "150px" }} />
        </Box>
        <Typography sx={styles.heading}>
          Where should customer replies go?
        </Typography>
        <Box sx={styles.subheading}>
          <Typography sx={styles.subheadingText}>
            Add an email to receive customer replies if they reply to email
            notifications sent via SnowSync platform.
          </Typography>
        </Box>
        <Box
          component="form"
          onSubmit={(e) => {
            e.preventDefault();
            submitForm();
          }}
        >
          <Grid container rowSpacing={2} direction="column">
            <Grid item marginBottom="20px">
              <TextField
                id="replyToEmail"
                placeholder="name@organization.com"
                value={values.replyToEmail}
                onChange={(e) => {
                  setBlurredSinceEdit(false);
                  handleChange(e);
                }}
                onBlur={(e) => {
                  setBlurredSinceEdit(true);
                  handleBlur(e);
                }}
                error={showError}
                helperText={showError && errors.replyToEmail}
                fullWidth
              />
            </Grid>
            <Grid item>
              <Button
                type="submit"
                variant="contained"
                disabled={!isValid || isLoading}
                fullWidth
                data-testid="reply-email-save-and-continue-button"
              >
                Save and Continue
              </Button>
            </Grid>
            <Grid item>
              <Button
                variant="outlined"
                fullWidth
                disabled={isLoading}
                onClick={goToDashboard}
                data-testid="reply-email-continue-without-email-button"
              >
                Continue Without Email
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Container>
    </FullScreenAuthLayout>
  );
};

const useStyles = makeSxStyles(({ palette: { grey } }) => ({
  heading: {
    fontFamily: "Inter",
    fontWeight: 700,
    fontSize: "32px",
    lineHeight: "40px",
    color: grey[100],
    textAlign: "center",
    mb: "40px",
  },
  subheading: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    mb: "40px",
  },
  subheadingText: {
    fontFamily: "Inter",
    fontSize: "14px",
    lineHeight: "20px",
    letterSpacing: "0.1px",
    color: grey[100],
    textAlign: "center",
  },
}));

export default ReplyEmailSetupPage;
